import axios from "axios";
import { getCookie, removeCookie, setCookie } from "../../cookies/cookies";
import { resolveApiBaseUrl } from "../../../utils/backendUrl";

const instance = axios.create();
instance.defaults.baseURL = resolveApiBaseUrl();

export async function callBackendApi(url, type = "GET", data = null, hasRetried = false, requestConfig = {}) {
  const retryFlag = typeof hasRetried === "boolean" ? hasRetried : false;
  const extraConfig = (typeof hasRetried === "object" && hasRetried !== null) ? hasRetried : requestConfig;

  // Set base URL
  instance.defaults.baseURL = resolveApiBaseUrl();

  let bearerToken = getCookie("launchcore");
  if (bearerToken) {
    instance.defaults.headers.common["Authorization"] = bearerToken;
  } else {
    delete instance.defaults.headers.common["Authorization"];
  }

  try {
    let response;
    const isFormData = data instanceof FormData; // Detect if data is FormData
    const method = String(type || "GET").toUpperCase();
    const isPutWithFormData = method === "PUT" && isFormData;

    // Set correct headers based on request type
    const headers = isFormData
      ? {}
      : { "Content-Type": "application/json" };

    if (isPutWithFormData && data && typeof data.get === "function" && !data.get("_method")) {
      data.append("_method", "PUT");
    }

    switch (method) {
      case "GET":
        response = await instance.get(url, { headers, ...extraConfig });
        break;
      case "POST":
        response = await instance.post(url, data, { headers, ...extraConfig });
        break;
      case "PUT":
        response = isPutWithFormData
          ? await instance.post(url, data, { headers, ...extraConfig })
          : await instance.put(url, data, { headers, ...extraConfig });
        break;
      case "DELETE":
        response = await instance.delete(url, { data, headers, ...extraConfig });
        break;
      default:
        throw new Error(`Unsupported request type: ${type}`);
    }

    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error) {
    if (
      error?.response?.status === 401 &&
      !retryFlag &&
      url !== "/login" &&
      url !== "/refresh-token" &&
      url !== "/login-oauth"
    ) {
      const refreshed = await refreshAccessToken();

      if (refreshed) {
        return callBackendApi(url, type, data, true, extraConfig);
      }

      removeCookie("launchcore");
      removeCookie("launchcore_refresh");
    }

    let errorMessage = "An unknown error occurred.";
    if (error.response) {
      const responseData = error.response.data;
      const validationErrors =
        responseData?.errors && typeof responseData.errors === "object" && !Array.isArray(responseData.errors)
          ? responseData.errors
          : null;

      if (typeof responseData === "string") {
        errorMessage = responseData;
      } else if (typeof responseData?.message === "string" && responseData.message.trim() !== "") {
        errorMessage = responseData.message;
      } else if (typeof responseData?.error === "string" && responseData.error.trim() !== "") {
        errorMessage = responseData.error;
      } else if (typeof responseData?.errors === "string" && responseData.errors.trim() !== "") {
        errorMessage = responseData.errors;
      } else if (validationErrors) {
        errorMessage = "Validation failed.";
      } else {
        errorMessage = error.response.statusText || "Request failed.";
      }

      return {
        success: false,
        error: errorMessage,
        validationErrors,
        errorData: responseData,
        status: error.response.status,
      };
    } else if (error.request) {
      errorMessage = "No response received from the server.";
    } else {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
}

async function refreshAccessToken() {
  const refreshToken = getCookie("launchcore_refresh");

  if (!refreshToken) {
    return false;
  }

  try {
    delete instance.defaults.headers.common["Authorization"];
    const response = await instance.post("/refresh-token", {
      refresh_token: refreshToken,
    });

    if (!response?.data?.access_token) {
      return false;
    }

    setCookie(
      "launchcore",
      `${response.data.token_type || "Bearer"} ${response.data.access_token}`
    );

    if (response.data.refresh_token) {
      setCookie("launchcore_refresh", response.data.refresh_token);
    }

    return true;
  } catch (error) {
    return false;
  }
}

/*
import { callBackendApi } from "./api";

// Example: Calling a GET API
(async () => {
    const response = await callBackendApi("/users", "GET");
    if (response.success) {
        console.log("Data:", response.data);
    } else {
        console.error("Error:", response.error);
    }
})();

// Example: Calling a POST API with data
(async () => {
    const payload = { name: "John", age: 30 };
    const response = await callBackendApi("/users", "POST", payload);
    if (response.success) {
        console.log("User created:", response.data);
    } else {
        console.error("Error:", response.error);
    }
})();

*/
